import React, { useState, useEffect } from "react";
import { FaCrown } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

const ProUpgradeButton = ({ userInfo }) => {
  const { t } = useTranslation();
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnWaitingList, setIsOnWaitingList] = useState(false);
  const [isCheckingWaitingList, setIsCheckingWaitingList] = useState(true);

  // Check if user is already on the waiting list when component mounts or userInfo changes
  useEffect(() => {
    const checkWaitingListStatus = async () => {
      if (!userInfo?.email) {
        setIsCheckingWaitingList(false);
        return;
      }

      try {
        // Check if document with user's email as ID exists
        const docRef = doc(db, "proWaitingList", userInfo.email);
        const docSnap = await getDoc(docRef);
        setIsOnWaitingList(docSnap.exists());
      } catch (error) {
        console.error("Error checking waiting list status:", error);
        // If we can't check, assume they're not on the list to allow them to try
        setIsOnWaitingList(false);
      } finally {
        setIsCheckingWaitingList(false);
      }
    };

    checkWaitingListStatus();
  }, [userInfo?.email]);

  const handleUpgradeClick = async () => {
    if (!userInfo?.email) {
      alert(t("settings.messages.pleaseLoginFirst"));
      return;
    }

    if (isOnWaitingList) {
      alert(t("settings.proUpgrade.alreadyOnWaitingList"));
      return;
    }

    setIsSubmitting(true);
    try {
      // Add user to the pro waiting list using email as document ID
      await setDoc(doc(db, "proWaitingList", userInfo.email), {
        email: userInfo.email,
        username: userInfo.username || userInfo.email,
        timestamp: serverTimestamp(),
        status: "pending",
      });
      setIsOnWaitingList(true);
      setShowPopup(true);
    } catch (error) {
      console.error("Error adding to waiting list:", error);
      alert(t("settings.messages.failedToJoinWaitingList"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={handleUpgradeClick}
        disabled={isSubmitting || isCheckingWaitingList || isOnWaitingList}
        className={`w-full px-6 py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 font-semibold ${
          isOnWaitingList
            ? "bg-primary/50 text-white cursor-not-allowed"
            : "bg-gradient-to-r from-test to-test/80 hover:from-test/90 hover:to-test/70 text-primary"
        }`}
      >
        <FaCrown className="w-5 h-5" />
        {isCheckingWaitingList
          ? t("settings.proUpgrade.checking")
          : isOnWaitingList
          ? t("settings.proUpgrade.alreadyOnList")
          : isSubmitting
          ? t("settings.proUpgrade.processing")
          : t("settings.proUpgrade.upgrade")}
      </button>

      {/* Waiting List Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-secondary rounded-2xl p-6 max-w-md w-full shadow-xl border border-test/20">
            <div className="text-center">
              <div className="w-16 h-16 bg-test/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCrown className="w-8 h-8 text-test" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {t("settings.messages.youreOnTheList")}
              </h3>
              <p className="text-white/80 mb-6">
                {t("settings.messages.waitingListSuccess")}
              </p>
              <button
                onClick={() => setShowPopup(false)}
                className="bg-test hover:bg-test/90 text-primary px-6 py-2 rounded-lg transition-colors duration-200"
              >
                {t("settings.messages.gotIt")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProUpgradeButton;
